import type { AdminUserDetailDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { KycBadge } from './UserBadges';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { formatDate, formatRelative } from '../../../utils/format';
import { placeText } from '../../../utils/place';

/**
 * Profile tab of the user detail drawer (AUT-124). Two info-grid sections
 * (Контакты / Аккаунт) + an activity stat row, mirroring the mockup.
 *
 * PII (phone / email) renders through `<RedactedField perm="view_pii">`,
 * agreeing with `dto.piiRevealed`. City / language / counts are not PII.
 */
const SectionTitle = ({ children }: { children: ReactNode }): ReactElement => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      color: tokens.text.tertiary,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const InfoItem = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <Box>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mb: 0.4 }}>{label}</Typography>
    <Box sx={{ fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {children}
    </Box>
  </Box>
);

const InfoGrid = ({ children }: { children: ReactNode }): ReactElement => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>{children}</Box>
);

const StatBox = ({ value, label }: { value: ReactNode; label: string }): ReactElement => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.75,
      bgcolor: 'background.paper',
    }}
  >
    <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px' }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: tokens.text.tertiary, mt: 0.25 }}>{label}</Typography>
  </Box>
);

export const ProfileTab = ({ user }: { user: AdminUserDetailDto }): ReactElement => {
  const { t } = useTranslation();
  const phone = user.phoneNumber;
  const email = user.email ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <SectionTitle>{t('users.detail.sections.contacts')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('users.detail.fields.phone')}>
            {user.piiRevealed ? (
              <RedactedField perm="view_pii" value={phone} redacted={phone} copyable />
            ) : (
              <RedactedField perm="view_pii" value={phone} redacted={phone} />
            )}
          </InfoItem>
          <InfoItem label={t('users.detail.fields.email')}>
            {email ? (
              user.piiRevealed ? (
                <RedactedField perm="view_pii" value={email} redacted={email} copyable />
              ) : (
                <RedactedField perm="view_pii" value={email} redacted={email} />
              )
            ) : (
              t('users.detail.noEmail')
            )}
          </InfoItem>
          <InfoItem label={t('users.detail.fields.city')}>
            {placeText(user) ?? t('users.detail.noValue')}
          </InfoItem>
          <InfoItem label={t('users.detail.fields.language')}>{user.language}</InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('users.detail.sections.account')}</SectionTitle>
        <InfoGrid>
          <InfoItem label={t('users.detail.fields.kycStatus')}>
            <KycBadge kycStatus={user.kycStatus} />
          </InfoItem>
          <InfoItem label={t('users.detail.fields.registered')}>
            {formatDate(user.createdAt)}
          </InfoItem>
          <InfoItem label={t('users.detail.fields.lastLogin')}>
            {user.lastLoginAt ? formatRelative(user.lastLoginAt) : t('users.detail.neverLoggedIn')}
          </InfoItem>
          <InfoItem label={t('users.detail.fields.country')}>
            {user.country ?? t('users.detail.noValue')}
          </InfoItem>
        </InfoGrid>
      </Box>

      <Box>
        <SectionTitle>{t('users.detail.sections.activity')}</SectionTitle>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
          <StatBox value={user.ordersSummary.total} label={t('users.detail.stats.orders')} />
          <StatBox value={user.appealsFiled} label={t('users.detail.stats.appeals')} />
        </Box>
      </Box>
    </Box>
  );
};
